import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Shell from "@/components/layout/Shell";
import ProtectedRoute from "@/components/ProtectedRoute";
import CookieBanner from "@/components/CookieBanner";
import InstallPWABanner from "@/components/InstallPWABanner";
import Landing from "@/pages/Landing";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Trust from "@/pages/Trust";
import HelpWidget from "@/components/HelpWidget";
import SplashLoader from "@/components/common/SplashLoader";

// Lazy-load every authenticated route so the landing bundle stays small.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Clients = lazy(() => import("@/pages/Clients"));
const Funds = lazy(() => import("@/pages/Funds"));
const Schemes = lazy(() => import("@/pages/Schemes"));
const Investors = lazy(() => import("@/pages/Investors"));
const InvestorDetail = lazy(() => import("@/pages/InvestorDetail"));
const KycReview = lazy(() => import("@/pages/KycReview"));
const Commitments = lazy(() => import("@/pages/Commitments"));
const CapitalCalls = lazy(() => import("@/pages/CapitalCalls"));
const Payments = lazy(() => import("@/pages/Payments"));
const BankRecon = lazy(() => import("@/pages/BankRecon"));
const Ledger = lazy(() => import("@/pages/Ledger"));
const ChartOfAccounts = lazy(() => import("@/pages/ChartOfAccounts"));
const Journal = lazy(() => import("@/pages/Journal"));
const Nav = lazy(() => import("@/pages/Nav"));
const Portfolio = lazy(() => import("@/pages/Portfolio"));
const InvestmentDetail = lazy(() => import("@/pages/InvestmentDetail"));
const Compliance = lazy(() => import("@/pages/Compliance"));
const Tasks = lazy(() => import("@/pages/Tasks"));
const Documents = lazy(() => import("@/pages/Documents"));
const Reports = lazy(() => import("@/pages/Reports"));
const Users = lazy(() => import("@/pages/Users"));
const Settings = lazy(() => import("@/pages/Settings"));
const InvestorPortal = lazy(() => import("@/pages/InvestorPortal"));
const InvestorDocs = lazy(() => import("@/pages/InvestorDocs"));
const InvestorStatements = lazy(() => import("@/pages/InvestorStatements"));
const BulkUpload = lazy(() => import("@/pages/BulkUpload"));
const AuditTrail = lazy(() => import("@/pages/AuditTrail"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const Distributions = lazy(() => import("@/pages/Distributions"));
const Security = lazy(() => import("@/pages/Security"));
const Profile = lazy(() => import("@/pages/Profile"));
const Distributors = lazy(() => import("@/pages/Distributors"));
const DistributorPortal = lazy(() => import("@/pages/DistributorPortal"));
const DistributorInvestorDetail = lazy(() => import("@/pages/DistributorInvestorDetail"));
const Billing = lazy(() => import("@/pages/Billing"));
const BillingAdmin = lazy(() => import("@/pages/BillingAdmin"));
const Ingestion = lazy(() => import("@/pages/Ingestion"));
const DigestRecipients = lazy(() => import("@/pages/DigestRecipients"));
const RazorpayRoute = lazy(() => import("@/pages/RazorpayRoute"));
const InvestorKYC = lazy(() => import("@/pages/InvestorKYC"));
const Derivatives = lazy(() => import("@/pages/Derivatives"));
const Waterfall = lazy(() => import("@/pages/Waterfall"));
const Investees = lazy(() => import("@/pages/Investees"));
const InvesteeDetail = lazy(() => import("@/pages/InvesteeDetail"));
const Branding = lazy(() => import("@/pages/Branding"));
const TenantBranding = lazy(() => import("@/pages/TenantBranding"));
const UserManual = lazy(() => import("@/pages/UserManual"));
const DealWorkflow = lazy(() => import("@/pages/DealWorkflow"));
const Ops = lazy(() => import("@/pages/Ops"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const FundClasses = lazy(() => import("@/pages/FundClasses"));
const Taxation = lazy(() => import("@/pages/Taxation"));
const OpenEnded = lazy(() => import("@/pages/OpenEnded"));
const HwmSidePockets = lazy(() => import("@/pages/HwmSidePockets"));
const InvestorRedemptions = lazy(() => import("@/pages/InvestorRedemptions"));
const Leads = lazy(() => import("@/pages/Leads"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const SignHistory = lazy(() => import("@/pages/SignHistory"));
const NudgePreview = lazy(() => import("@/pages/NudgePreview"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const OwnerSitemap = lazy(() => import("@/pages/OwnerSitemap"));
const Demos = lazy(() => import("@/pages/Demos"));
const PlatformPractices = lazy(() => import("@/pages/PlatformPractices"));
const PlatformImpersonate = lazy(() => import("@/pages/PlatformImpersonate"));
const OwnerAuditLog = lazy(() => import("@/pages/OwnerAuditLog"));
const OwnerLandingConfig = lazy(() => import("@/pages/OwnerLandingConfig"));
const OwnerSupportInbox = lazy(() => import("@/pages/OwnerSupportInbox"));
const OwnerAnnouncements = lazy(() => import("@/pages/OwnerAnnouncements"));
const ReportTemplates = lazy(() => import("@/pages/ReportTemplates"));
const LeegalitySettings = lazy(() => import("@/pages/LeegalitySettings"));
const OwnerTrash = lazy(() => import("@/pages/OwnerTrash"));
const OwnerBulkCleanup = lazy(() => import("@/pages/OwnerBulkCleanup"));
const ProposalTemplates = lazy(() => import("@/pages/ProposalTemplates"));
const PlatformBrand = lazy(() => import("@/pages/PlatformBrand"));

function RouteFallback() {
  return <SplashLoader compact />;
}

function AppRouter() {
  const location = useLocation();
  // Process OAuth callback synchronously during render — see Emergent Auth playbook
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/trust" element={<Trust />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route element={<ProtectedRoute><Shell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/funds" element={<Funds />} />
        <Route path="/schemes" element={<Schemes />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/investors/:id" element={<InvestorDetail />} />
        <Route path="/kyc-review" element={<KycReview />} />
        <Route path="/commitments" element={<Commitments />} />
        <Route path="/capital-calls" element={<CapitalCalls />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/bank-recon" element={<BankRecon />} />
        <Route path="/ledger" element={<Ledger />} />
        <Route path="/chart-of-accounts" element={<ChartOfAccounts />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/nav" element={<Nav />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/portfolio/:id" element={<InvestmentDetail />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/report-templates" element={<ReportTemplates />} />
        <Route path="/users" element={<Users />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/razorpay" element={<RazorpayRoute />} />
        <Route path="/settings/leegality" element={<LeegalitySettings />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/proposal-templates" element={<ProposalTemplates />} />
        <Route path="/bulk-upload" element={<BulkUpload />} />
        <Route path="/audit" element={<AuditTrail />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/distributions" element={<Distributions />} />
        <Route path="/security" element={<Security />} />
        <Route path="/security/change-password" element={<ChangePassword />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/distributors" element={<Distributors />} />
        <Route path="/distributors/:id" element={<Distributors />} />
        <Route path="/distributor" element={<DistributorPortal />} />
        <Route path="/distributor/investor/:investor_id" element={<DistributorInvestorDetail />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/admin/billing" element={<BillingAdmin />} />
        <Route path="/admin/digest" element={<DigestRecipients />} />
        <Route path="/ingestion" element={<Ingestion />} />
        <Route path="/investor/kyc" element={<InvestorKYC />} />
        <Route path="/compliance/history" element={<SignHistory />} />
        <Route path="/admin/lp-nudge" element={<NudgePreview />} />
        <Route path="/admin/pricing" element={<Pricing />} />
        <Route path="/admin/demos" element={<Demos />} />
        <Route path="/platform/practices" element={<PlatformPractices />} />
        <Route path="/platform/impersonate" element={<PlatformImpersonate />} />
        <Route path="/owner/audit-log" element={<OwnerAuditLog />} />
        <Route path="/owner/landing" element={<OwnerLandingConfig />} />
        <Route path="/owner/support" element={<OwnerSupportInbox />} />
        <Route path="/owner/whats-new" element={<OwnerAnnouncements />} />
        <Route path="/owner/trash" element={<OwnerTrash />} />
        <Route path="/owner/bulk-cleanup" element={<OwnerBulkCleanup />} />
        <Route path="/owner/sitemap" element={<OwnerSitemap />} />
        <Route path="/owner/brand" element={<PlatformBrand />} />
        <Route path="/derivatives" element={<Derivatives />} />
        <Route path="/waterfall" element={<Waterfall />} />
        <Route path="/investees" element={<Investees />} />
        <Route path="/investees/:id" element={<InvesteeDetail />} />
        <Route path="/branding" element={<Branding />} />
        <Route path="/settings/branding" element={<TenantBranding />} />
        <Route path="/help" element={<UserManual />} />
        <Route path="/deal-workflow" element={<DealWorkflow />} />
        <Route path="/ops" element={<Ops />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/fund-classes" element={<FundClasses />} />
        <Route path="/taxation" element={<Taxation />} />
        <Route path="/open-ended" element={<OpenEnded />} />
        <Route path="/hwm" element={<HwmSidePockets />} />

        <Route path="/investor" element={<InvestorPortal />} />
        <Route path="/investor/documents" element={<InvestorDocs />} />
        <Route path="/investor/statements" element={<InvestorStatements />} />
        <Route path="/investor/redeem" element={<InvestorRedemptions />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={150} skipDelayDuration={100}>
        <BrowserRouter>
          <PlatformBrandBoot />
          <AppRouter />
          <HelpWidget />
          <CookieBanner />
          <InstallPWABanner />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  );
}

/**
 * Runtime favicon + landing-footer-logo swap.
 * Fetches the Owner-configured global logo on mount and swaps the
 * `<link rel="icon">` href + any static <img id="landing-footer-logo"> src.
 * Also listens for "aif-platform-brand-updated" to re-swap live after
 * the Owner uploads a new logo, so no reload is needed.
 */
function PlatformBrandBoot() {
  React.useEffect(() => {
    const apply = async () => {
      try {
        const { api } = await import("@/lib/api");
        const { data } = await api.get("/platform/brand");
        if (!data?.logo_url) return;
        // Favicon
        const setLink = (rel, href) => {
          let el = document.querySelector(`link[rel="${rel}"]`);
          if (!el) {
            el = document.createElement("link");
            el.rel = rel;
            document.head.appendChild(el);
          }
          el.href = href;
        };
        setLink("icon", data.logo_url);
        setLink("apple-touch-icon", data.logo_url);
        // Landing footer static img
        const img = document.getElementById("landing-footer-logo");
        if (img) img.src = data.logo_url;
        // Boot splash still-visible on very first paint
        const splash = document.querySelector("#brand-splash img");
        if (splash) splash.src = data.logo_url;
      } catch { /* ignore */ }
    };
    apply();
    const h = () => apply();
    window.addEventListener("aif-platform-brand-updated", h);
    return () => window.removeEventListener("aif-platform-brand-updated", h);
  }, []);
  return null;
}
